import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { formatPrice } from "@/components/ProductCard";
import { toast } from "sonner";
import { claimStatusMeta, CLOSED_STATES } from "@/lib/claims";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  CaretDown, PaperPlaneRight, ChatCircleDots, DownloadSimple, CheckCircle, Wallet,
  Truck, ArrowsClockwise, Package, CurrencyDollar, Clock, Warning, UploadSimple,
} from "@phosphor-icons/react";

const PLACEHOLDER = "https://images.unsplash.com/photo-1495100497150-fe209c585f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const inputCls = "w-full border border-black/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30";
const ENVIA_CARRIERS = ["estafeta", "fedex", "dhl", "redpack", "paquetexpress", "carssa", "ups"];

function Timeline({ messages = [] }) {
  return (
    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
      {messages.map((m) => {
        if (m.system) return <p key={m.id} className="text-[11px] text-muted-foreground text-center py-1">· {m.text} ·</p>;
        const admin = m.author_role === "admin";
        return (
          <div key={m.id} className={`flex ${admin ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${admin ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              <p className="text-[10px] font-bold opacity-80 mb-0.5">{admin ? m.author_name : `Cliente · ${m.author_name}`}</p>
              <p>{m.text}</p>
              <p className="text-[9px] opacity-60 mt-0.5">{new Date(m.created_at).toLocaleString("es-MX")}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GuideBox({ title, guide }) {
  if (!guide) return null;
  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-sm">
      <p className="font-semibold text-indigo-800">{title}</p>
      <p className="text-xs text-indigo-700 mt-0.5"><b>{guide.carrier}</b> · Guía {guide.tracking_number}</p>
      {guide.instructions && <p className="text-xs text-indigo-700 mt-1">{guide.instructions}</p>}
      <div className="flex gap-3 mt-2">
        {guide.label_url && <a href={fileUrl(guide.label_url)} target="_blank" rel="noreferrer" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"><DownloadSimple weight="bold" size={14} /> Descargar guía</a>}
        {guide.track_url && <a href={guide.track_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Rastrear</a>}
        {guide.manual && <span className="text-[10px] text-muted-foreground">(cargada manualmente)</span>}
      </div>
    </div>
  );
}

function VerdictPanel({ claim, onUpdate }) {
  const [choice, setChoice] = useState(null);
  const [amount, setAmount] = useState(String(Math.round(claim.order_total || 0)));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (verdict) => {
    setBusy(true);
    try {
      const body = { verdict, note };
      if (verdict !== "cambio") body.amount = parseFloat(amount) || claim.order_total;
      const r = await api.post(`/admin/claims/${claim.id}/verdict`, body);
      toast.success("Veredicto registrado");
      onUpdate(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const OPTS = [
    { k: "cambio", label: "Cambio de producto", desc: "El cliente devuelve y recibe un reemplazo." },
    { k: "saldo", label: "Devolución + saldo a favor", desc: "Se acredita saldo al confirmar la devolución." },
    { k: "reembolso", label: "Devolución + reembolso", desc: "Se reembolsa a la misma forma de pago." },
  ];

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="verdict-panel">
      <p className="text-sm font-bold flex items-center gap-1"><Warning weight="bold" size={15} className="text-primary" /> Emitir veredicto</p>
      <div className="grid gap-2">
        {OPTS.map((o) => (
          <label key={o.k} className={`flex items-start gap-2 border rounded-md px-3 py-2 text-sm cursor-pointer ${choice === o.k ? "border-primary ring-1 ring-primary/30 bg-white" : "border-black/10 bg-white"}`}>
            <input type="radio" name={`verdict-${claim.id}`} checked={choice === o.k} onChange={() => setChoice(o.k)} data-testid={`verdict-opt-${o.k}`} className="mt-0.5" />
            <span><b>{o.label}</b><span className="block text-xs text-muted-foreground">{o.desc}</span></span>
          </label>
        ))}
      </div>
      {choice && choice !== "cambio" && (
        <div>
          <label className="text-xs font-bold uppercase tracking-wide">Monto ({choice === "saldo" ? "saldo a favor" : "reembolso"})</label>
          <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="verdict-amount" className={inputCls} />
        </div>
      )}
      {choice && (
        <>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota para el cliente (opcional)" className={inputCls} />
          <button onClick={() => submit(choice)} disabled={busy} data-testid="submit-verdict-button" className="nb-btn bg-primary text-primary-foreground px-4 py-2 text-sm hover:brightness-110 disabled:opacity-50">{busy ? "Guardando…" : "Confirmar veredicto"}</button>
        </>
      )}
    </div>
  );
}

function GuideGenerator({ claim, kind, onUpdate }) {
  const [carrier, setCarrier] = useState("estafeta");
  const [instructions, setInstructions] = useState("");
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  const [mCarrier, setMCarrier] = useState("");
  const [mTracking, setMTracking] = useState("");
  const [mLabel, setMLabel] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const isReturn = kind === "return";

  const gen = async () => {
    setBusy(true);
    try {
      const path = isReturn ? "return-guide" : "replacement-guide";
      const body = { carrier };
      if (isReturn) body.instructions = instructions;
      const r = await api.post(`/admin/claims/${claim.id}/${path}`, body);
      toast.success("Guía generada");
      onUpdate(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo generar la guía", { duration: 7000 }); }
    finally { setBusy(false); }
  };

  const uploadLabel = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/admin/upload/label", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMLabel(res.data.path);
      toast.success("Archivo subido");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submitManual = async () => {
    if (!mCarrier.trim() || !mTracking.trim()) { toast.error("Indica paquetería y número de guía"); return; }
    setBusy(true);
    try {
      const path = isReturn ? "return-guide-manual" : "replacement-guide-manual";
      const body = { carrier: mCarrier.trim(), tracking_number: mTracking.trim(), label_url: mLabel };
      if (isReturn) body.instructions = instructions;
      const r = await api.post(`/admin/claims/${claim.id}/${path}`, body);
      toast.success("Guía cargada");
      onUpdate(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-[#E8662B]/30 bg-[#E8662B]/5 p-3 space-y-2">
      <p className="text-sm font-bold flex items-center gap-1"><Truck weight="bold" size={15} className="text-[#E8662B]" /> {isReturn ? "Generar guía de devolución (cliente → tienda)" : "Generar guía del reemplazo (tienda → cliente)"}</p>
      {!manual ? (
        <>
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <label className="text-[10px] text-muted-foreground block">Paquetería</label>
              <Select value={carrier} onValueChange={setCarrier}>
                <SelectTrigger data-testid={`claim-carrier-${claim.id}`} className="h-9 w-40 text-sm rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-md">{ENVIA_CARRIERS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <button onClick={gen} disabled={busy} data-testid={`generate-${kind}-guide-${claim.id}`} className="nb-btn bg-[#E8662B] text-white h-9 px-3 text-sm flex items-center gap-1 hover:brightness-110 disabled:opacity-50">
              <Truck weight="bold" size={14} /> {busy ? "Generando…" : "Generar guía con Envia"}
            </button>
          </div>
          {isReturn && <textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Instrucciones para el cliente (opcional)" className={inputCls} />}
          <button type="button" onClick={() => setManual(true)} data-testid={`manual-toggle-${kind}-${claim.id}`} className="text-xs text-primary hover:underline">¿Envia no deja generar? Subir guía manualmente</button>
        </>
      ) : (
        <div className="space-y-2" data-testid={`manual-guide-${kind}-${claim.id}`}>
          <p className="text-xs text-muted-foreground">Carga una guía externa (número + archivo PDF/imagen opcional).</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground">Paquetería</label>
              <input value={mCarrier} onChange={(e) => setMCarrier(e.target.value)} placeholder="Ej. Estafeta" data-testid={`manual-carrier-${claim.id}`} className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">Número de guía</label>
              <input value={mTracking} onChange={(e) => setMTracking(e.target.value)} placeholder="Ej. 1234567890" data-testid={`manual-tracking-${claim.id}`} className={inputCls} />
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={uploadLabel} className="hidden" />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`manual-upload-${claim.id}`} className="nb-btn bg-secondary px-3 py-2 text-sm flex items-center gap-1"><UploadSimple weight="bold" size={14} /> {uploading ? "Subiendo…" : (mLabel ? "Cambiar archivo" : "Subir guía (PDF/imagen)")}</button>
            {mLabel && <a href={fileUrl(mLabel)} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">ver archivo</a>}
          </div>
          {isReturn && <textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} placeholder="Instrucciones para el cliente (opcional)" className={inputCls} />}
          <div className="flex gap-2">
            <button onClick={submitManual} disabled={busy} data-testid={`submit-manual-${kind}-${claim.id}`} className="nb-btn bg-primary text-primary-foreground px-4 py-2 text-sm hover:brightness-110 disabled:opacity-50">{busy ? "Guardando…" : "Guardar guía"}</button>
            <button type="button" onClick={() => setManual(false)} className="text-xs text-muted-foreground hover:text-foreground px-2">Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

function RefundForm({ claim, onUpdate }) {
  const [amount, setAmount] = useState(String(Math.round(claim.resolution_amount || claim.order_total || 0)));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/claims/${claim.id}/refund`, { amount: parseFloat(amount) || 0, date, reference });
      toast.success("Reembolso registrado");
      onUpdate(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 space-y-2" data-testid="refund-form">
      <p className="text-sm font-bold flex items-center gap-1 text-emerald-800"><CurrencyDollar weight="bold" size={15} /> Registrar reembolso</p>
      <div className="grid grid-cols-3 gap-2">
        <div><label className="text-[10px] text-muted-foreground">Monto</label><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="refund-amount" className={inputCls} /></div>
        <div><label className="text-[10px] text-muted-foreground">Fecha</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="refund-date" className={inputCls} /></div>
        <div><label className="text-[10px] text-muted-foreground">Referencia</label><input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ref. pago" data-testid="refund-reference" className={inputCls} /></div>
      </div>
      <button onClick={submit} disabled={busy} data-testid="submit-refund-button" className="nb-btn bg-emerald-600 text-white px-4 py-2 text-sm hover:brightness-110 disabled:opacity-50">{busy ? "Guardando…" : "Registrar y cerrar"}</button>
    </div>
  );
}

function AdminClaimDetail({ claim, onUpdate }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const closed = CLOSED_STATES.includes(claim.status);
  const noVerdict = !claim.verdict && !closed;

  const act = async (fn, okMsg) => { try { const r = await fn(); if (okMsg) toast.success(okMsg); onUpdate(r.data); } catch (e) { toast.error(e.response?.data?.detail || "Error"); } };
  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try { const r = await api.post(`/claims/${claim.id}/message`, { text: msg.trim() }); setMsg(""); onUpdate(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); } finally { setSending(false); }
  };

  const addr = claim.shipping_address || {};

  return (
    <div className="border-t border-black/10 p-4 space-y-4 text-sm">
      {claim.status === "abierto" && (
        <button onClick={() => act(() => api.post(`/admin/claims/${claim.id}/attend`), "Reclamo en revisión")} data-testid={`attend-claim-${claim.id}`} className="nb-btn bg-primary text-primary-foreground px-4 py-2 text-sm hover:brightness-110">Atender reclamo</button>
      )}
      {claim.status === "esperando_cliente" && (
        <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2 text-xs text-orange-700 flex items-center gap-2"><Clock weight="bold" size={15} /> Esperando respuesta del cliente. Si no responde en 24 h el reclamo se cerrará automáticamente.</div>
      )}

      {/* Order + motivo + evidencia */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Pedido {claim.order_number} · {formatPrice(claim.order_total)}</p>
          {(claim.items || []).map((it, i) => (
            <div key={i} className="flex items-center gap-2 py-0.5">
              <img src={it.product_image ? fileUrl(it.product_image) : PLACEHOLDER} alt="" className="w-8 h-8 rounded object-cover" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
              <span className="text-xs">{it.product_title} ×{it.quantity}</span>
            </div>
          ))}
          <p className="text-xs text-muted-foreground mt-2"><b>Cliente:</b> {claim.buyer_name} · {claim.buyer_email}</p>
          {addr.street && <p className="text-xs text-muted-foreground">{[addr.street, addr.number, addr.city, addr.state, addr.zip].filter(Boolean).join(", ")}</p>}
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Motivo</p>
          <p>{claim.reason}</p>
          {claim.description && <p className="text-muted-foreground text-xs mt-1">{claim.description}</p>}
          {(claim.photos || []).length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {claim.photos.map((p, i) => <a key={i} href={fileUrl(p)} target="_blank" rel="noreferrer"><img src={fileUrl(p)} alt="" className="w-14 h-14 rounded object-cover border border-black/10" /></a>)}
            </div>
          )}
        </div>
      </div>

      {/* Verdict / resolution actions */}
      {noVerdict && (claim.status === "en_revision" || claim.status === "esperando_cliente") && <VerdictPanel claim={claim} onUpdate={onUpdate} />}

      {["cambio_aceptado", "saldo_por_devolver", "reembolso_por_devolver"].includes(claim.status) && <GuideGenerator claim={claim} kind="return" onUpdate={onUpdate} />}
      {claim.verdict === "cambio" && claim.status === "cambio_por_aceptar" && <p className="text-xs text-muted-foreground">Esperando que el cliente acepte el cambio.</p>}

      <GuideBox title="Guía de devolución (cliente → tienda)" guide={claim.return_guide} />

      {["cambio_devolucion", "saldo_devolucion", "reembolso_devolucion"].includes(claim.status) && (
        <button onClick={() => act(() => api.post(`/admin/claims/${claim.id}/confirm-return`), "Devolución confirmada")} data-testid={`confirm-return-${claim.id}`} className="nb-btn bg-[hsl(var(--meli-green))] text-white px-4 py-2 text-sm hover:brightness-110 flex items-center gap-1"><Package weight="bold" size={15} /> Confirmar recepción de la devolución</button>
      )}

      {claim.status === "cambio_recibido" && <GuideGenerator claim={claim} kind="replacement" onUpdate={onUpdate} />}
      <GuideBox title="Guía del reemplazo (tienda → cliente)" guide={claim.replacement_guide} />

      {claim.status === "cambio_reemplazo" && (
        <button onClick={() => act(() => api.post(`/admin/claims/${claim.id}/close`), "Reclamo cerrado")} data-testid={`close-claim-${claim.id}`} className="nb-btn bg-primary text-primary-foreground px-4 py-2 text-sm hover:brightness-110">Cerrar reclamo</button>
      )}

      {claim.status === "reembolso_por_registrar" && <RefundForm claim={claim} onUpdate={onUpdate} />}

      {claim.store_credit_amount > 0 && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800 flex items-center gap-2"><Wallet weight="bold" size={16} /> Saldo a favor acreditado: <b>{formatPrice(claim.store_credit_amount)}</b></div>}
      {claim.refund && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-emerald-800">Reembolso: <b>{formatPrice(claim.refund.amount)}</b> · {claim.refund.date}{claim.refund.reference ? ` · Ref. ${claim.refund.reference}` : ""} · por {claim.refund.registered_by}</div>}

      {/* Chat */}
      <div>
        <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1"><ChatCircleDots weight="bold" size={14} /> Historial y conversación</p>
        <Timeline messages={claim.messages} />
      </div>
      {!closed && (
        <div className="flex gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Responder al cliente… (activa la ventana de 24 h)" data-testid={`admin-claim-message-${claim.id}`} className={inputCls} />
          <button onClick={send} disabled={sending} data-testid={`admin-claim-send-${claim.id}`} className="nb-btn bg-primary text-primary-foreground px-4 hover:brightness-110 disabled:opacity-50"><PaperPlaneRight weight="bold" size={16} /></button>
        </div>
      )}
    </div>
  );
}

export function ClaimsAdminTab() {
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    const url = filter ? `/admin/claims?status=${filter}` : "/admin/claims";
    setClaims((await api.get(url)).data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateOne = (updated) => setClaims((list) => list.map((c) => (c.id === updated.id ? updated : c)));

  const FILTERS = [["active", "Activos"], ["", "Todos"], ["cerrado", "Cerrados"], ["cerrado_sin_respuesta", "Sin respuesta"]];

  return (
    <div data-testid="admin-claims">
      <div className="flex gap-1 mb-4 flex-wrap">
        {FILTERS.map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`claim-filter-${k || "all"}`}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === k ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground hover:bg-secondary"}`}>{label}</button>
        ))}
      </div>
      {loading ? <p className="text-muted-foreground">Cargando…</p> : claims.length === 0 ? (
        <div className="bg-white rounded-lg nb-shadow p-8 text-center text-muted-foreground">Sin reclamos</div>
      ) : (
        <div className="space-y-3">
          {claims.map((c) => {
            const meta = claimStatusMeta(c.status);
            return (
              <div key={c.id} className="bg-white rounded-lg nb-shadow overflow-hidden" data-testid={`admin-claim-${c.id}`}>
                <button onClick={() => setOpen(open === c.id ? null : c.id)} className="w-full p-4 flex items-center gap-3 text-left hover:bg-secondary/30">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-sm">{c.folio}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{c.buyer_name} · Pedido {c.order_number} · {c.reason} · {new Date(c.created_at).toLocaleDateString("es-MX")}</p>
                  </div>
                  <CaretDown weight="bold" size={16} className={`transition-transform ${open === c.id ? "rotate-180" : ""}`} />
                </button>
                {open === c.id && <AdminClaimDetail claim={c} onUpdate={updateOne} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
